// apps/client/src/world/spaces/vroom.ts (M2-T11) — a villager's home (spec §9.13): the toy-room shell (M1-T14 interior.ts) papered and
// floored from their house theme, the theme's furniture at its footprint centres (M1 rotation rule), and the villager standing just
// inside the door. Interiors are generated from the villager's data (theme preset), never stored. The villager animates through
// SpaceScene.update (rev 5 item 12; three.js never calls onBeforeRender on a Group).
import { getItem, tuning, villagerBank, type SurfaceEntry } from '@whiskerton/content';
import { parseSpace, type Rect, type Vec2, type VillagerId, type VillagerPublic } from '@whiskerton/shared';
import { footprintOf } from '@whiskerton/shared/rules/placement';
import { villagerIndoorSpot } from '@whiskerton/shared/villagers/schedule';
import * as THREE from 'three';
import type { AssetService } from '../../assets/service';
import { Avatar } from '../../characters/avatar';
import { villagerToDoll } from '../../characters/look';
import type { Game } from '../../game/game';
import type { SpaceScene, SpaceSceneDef } from '../../game/spaces';
import { trackVillagerAvatar, type VillagerAvatar } from '../../game/systems/villagers';
import { useGame } from '../../state/store';
import { interiorScene, type RoomBuildOptions } from '../interior';

/** Where a walking resident's centre stays away from the walls (about the resident's radius; the open south edge reaches the door). */
const WALL = 0.3, FRONT = 0.1;
const themeOf = (v: Pick<VillagerPublic, 'houseTheme'>) => villagerBank.houseThemes.find((t) => t.id === v.houseTheme) ?? villagerBank.houseThemes[0]!;

/** Interiors are generated from the villager's data (theme preset), never stored (spec §9.13). */
export async function buildVillagerRoom(v: VillagerPublic, assets: Pick<AssetService, 'model'>,
  deps: { avatar: (v: VillagerPublic) => Promise<VillagerAvatar>; room?: RoomBuildOptions }): Promise<SpaceScene> {
  const theme = themeOf(v);
  const [w, h] = theme.room;
  const pattern = (id: string) => (getItem(id) as SurfaceEntry).pattern;
  const { scene, room } = interiorScene({ w, h, wallpaper: pattern(theme.wallpaper), floor: pattern(theme.floor) }, deps.room);
  const solid: Rect[] = [];
  const furniture = await Promise.all(theme.furniture.map(async (f) => {
    const e = getItem(f.catalogId);
    const { w: fw, d: fd } = footprintOf({ catalogId: f.catalogId, rot: f.rot });
    const rug = e.kind === 'furniture' && e.category === 'rug';
    const obj = await assets.model(f.catalogId);
    obj.position.set(f.x + fw / 2, rug ? 0.004 : 0, f.y + fd / 2);       // M1 rotation rule: centre of the rotated footprint
    obj.rotation.y = -f.rot * Math.PI / 2;
    if (!rug) solid.push({ x: f.x, y: f.y, w: fw, h: fd });
    return obj;
  }));
  const furn = new THREE.Group(); furn.name = 'furniture';
  if (furniture.length) furn.add(...furniture);
  scene.add(furn);
  const av = await deps.avatar(v);
  const spot = villagerIndoorSpot(theme.room);
  av.root.position.set(spot.x, 0, spot.y);
  av.setMove('idle', 0);
  scene.add(av.root);
  const untrack = trackVillagerAvatar(v.id, av);
  const walkable = (p: Vec2) => p.x > WALL && p.y > WALL && p.x < w - WALL && p.y < h - FRONT
    && !solid.some((b) => p.x > b.x && p.x < b.x + b.w && p.y > b.y && p.y < b.y + b.h);
  return {
    scene, bounds: { x: 0, y: 0, w, h }, heightAt: () => 0, walkable, canMove: (_from, to) => walkable(to),
    update: (dt) => av.update(dt),            // rev 5 item 12: driven every frame by game/systems/spaces.ts (onBeforeRender never fires on a Group)
    tick: (_dt, minute) => room.light(minute),   // light and look: the home follows the town clock
    dispose: () => { untrack(); av.dispose(); room.dispose(); },
  };
}

/** The villager's room once their record is in the store. A first snapshot taken inside a villager house (a reload within the presence
 *  grace) builds the space before the villagers handler has applied the snapshot's villager list, so the room fills in when it lands. */
function villagerRoom(id: VillagerId, game: Game): Promise<SpaceScene> | SpaceScene {
  const make = (v: VillagerPublic) => buildVillagerRoom(v, game.assets,
    { avatar: async (x) => Avatar.create(await game.assets.rig(), villagerToDoll(x), tuning.move.residentHeight) });
  const known = useGame.getState().villagersList.find((x) => x.id === id);
  if (known) return make(known);
  const scene = new THREE.Scene();
  scene.background = new THREE.Color('#F3E4C6');
  const [w0, h0] = villagerBank.houseThemes[0]!.room;
  let inner: SpaceScene | null = null, gone = false;
  const unsub = useGame.subscribe((s) => {
    const v = s.villagersList.find((x) => x.id === id);
    if (!v) return;
    unsub();
    void make(v).then((built) => {
      if (gone) { built.dispose(); return; }
      inner = built;
      scene.add(...built.scene.children);
    });
  });
  return {
    scene,
    get bounds() { return inner?.bounds ?? { x: 0, y: 0, w: w0, h: h0 }; },
    heightAt: () => 0,
    walkable: (p) => inner?.walkable(p) ?? false,
    canMove: (a, b) => inner?.canMove?.(a, b) ?? false,
    update: (dt, now) => inner?.update?.(dt, now),
    tick: (dt, minute) => inner?.tick?.(dt, minute),
    dispose: () => { gone = true; unsub(); inner?.dispose(); },
  };
}

export const space: SpaceSceneDef = {
  kind: 'vroom',
  async build(snap, game) {
    const id = snap.kind === 'vroom' ? snap.villager : (parseSpace(snap.space) as { villager: VillagerId }).villager;
    return villagerRoom(id, game);
  },
};
